import React, { useState, useContext } from 'react';
import { Box, Typography, Card, CardContent, Button, Grid, Chip, CircularProgress, Alert, TextField, Divider } from '@mui/material';
import { Download, Assessment, People, EventNote, CheckCircle, Cancel, Queue, TrendingUp, Map } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import * as XLSX from 'xlsx';

const API = '/api';

const REPORTS = [
  { id:'leads', label:'Master Lead Register', icon:<People/>, color:'#003E6F', bg:'#EBF3FB', desc:'All leads with master data fields — name, phone, state, city, consumption, status, telecaller', adminOnly:false },
  { id:'telecaller-performance', label:'Telecaller Performance', icon:<TrendingUp/>, color:'#1565C0', bg:'#E3F0FB', desc:'Performance metrics per telecaller — calls, conversions, appointments, recall queue', adminOnly:true },
  { id:'appointments', label:'Appointment Report', icon:<EventNote/>, color:'#6A1B9A', bg:'#F5EEFE', desc:'All appointments with telecaller remarks, salesperson, status, location', adminOnly:false },
  { id:'telecaller-salesperson', label:'Telecaller → Salesperson Assignments', icon:<People/>, color:'#C2410C', bg:'#FFF3E8', desc:'Count of leads each telecaller has assigned to each salesperson', adminOnly:true },
  { id:'converted', label:'Converted Leads', icon:<CheckCircle/>, color:'#1B7A3E', bg:'#E8F5EE', desc:'Successfully converted leads with telecaller, salesperson and appointment details', adminOnly:true },
  { id:'rejected', label:'Non-Interested Analysis', icon:<Cancel/>, color:'#B91C1C', bg:'#FEF2F2', desc:'Rejection analysis with reasons, telecaller and customer consumption data', adminOnly:true },
  { id:'recalls', label:'On Hold / Recall Queue', icon:<Queue/>, color:'#E88A00', bg:'#FFF4E0', desc:'Full recall queue with hold reasons, scheduled times, overdue status', adminOnly:false },
  { id:'daily-activity', label:'Daily Activity Summary', icon:<Assessment/>, color:'#0D7A6F', bg:'#E0F5F3', desc:'Day-wise activity per user — calls, appointments booked, recalls set', adminOnly:true },
  { id:'geographic', label:'Geographic Performance', icon:<Map/>, color:'#7B1FA2', bg:'#F5EEFE', desc:'State and city-wise lead funnel — total, qualified, converted, conversion rate', adminOnly:true },
];

export default function Reports() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const isAdmin = ['admin','superadmin'].includes(user?.role);
  const hdr = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };

  const downloadReport = async (reportId) => {
    try {
      setLoading(l => ({...l, [reportId]:true}));
      setError('');
      const url = reportId==='daily-activity'
        ? `${API}/reports/${reportId}?date=${date}`
        : `${API}/reports/${reportId}`;
      const r = await axios.get(url, hdr);
      const { rows, report } = r.data;
      if (!rows || rows.length === 0) {
        setError('No data found for this report.');
        return;
      }
      // Create Excel workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      // Auto column widths
      const cols = Object.keys(rows[0]).map(k => ({ wch: Math.max(k.length, 15) }));
      ws['!cols'] = cols;
      XLSX.utils.book_append_sheet(wb, ws, report.slice(0,31));
      // Add metadata sheet
      const meta = XLSX.utils.aoa_to_sheet([
        ['Report Name', report],
        ['Generated By', user?.name || user?.email],
        ['Generated On', new Date().toLocaleString('en-IN')],
        ['Total Records', rows.length],
        ['Organisation', 'Anvil Energy Pvt. Ltd.'],
        ['Platform', 'TeleConnect Pro v1.2.0'],
      ]);
      XLSX.utils.book_append_sheet(wb, meta, 'Info');
      XLSX.writeFile(wb, `${report.replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.xlsx`);
      setSuccess(`${report} downloaded — ${rows.length} records`);
      setTimeout(()=>setSuccess(''),4000);
    } catch(e) {
      setError(e.response?.data?.message || 'Failed to download report');
    } finally {
      setLoading(l => ({...l, [reportId]:false}));
    }
  };

  const visibleReports = REPORTS.filter(r => !r.adminOnly || isAdmin);

  return (
    <Box sx={{ p:3, maxWidth:1200 }}>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Assessment sx={{ fontSize:32, color:'#003E6F' }}/>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#003E6F">Reports & Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Download reports as Excel (.xlsx) files</Typography>
        </Box>
        <Chip label={`${visibleReports.length} reports available`} size="small" sx={{ ml:'auto', bgcolor:'#EBF3FB', color:'#003E6F', fontWeight:600 }}/>
      </Box>

      <Divider sx={{ mb:3 }}/>

      {error && <Alert severity="error" sx={{ mb:2 }} onClose={()=>setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2 }} onClose={()=>setSuccess('')}>{success}</Alert>}

      {/* Date picker for daily report */}
      {isAdmin && (
        <Box sx={{ mb:3, p:2, bgcolor:'#F7FAFD', borderRadius:2, border:'1px solid #D0DFF0', display:'flex', alignItems:'center', gap:2 }}>
          <Typography fontSize={13} fontWeight={600} color="#003E6F">Date for Daily Activity Report:</Typography>
          <TextField type="date" size="small" value={date} onChange={e=>setDate(e.target.value)}
            sx={{ width:180 }} InputProps={{ sx:{ fontSize:13 } }}/>
        </Box>
      )}

      <Grid container spacing={2.5}>
        {visibleReports.map(report => (
          <Grid item xs={12} sm={6} md={6} lg={4} key={report.id}>
            <Card sx={{ borderRadius:2, border:`1px solid ${report.color}30`, height:'100%',
              boxShadow:'0 2px 12px rgba(0,62,111,0.08)', transition:'all 0.2s',
              '&:hover':{ boxShadow:'0 4px 20px rgba(0,62,111,0.15)', transform:'translateY(-2px)' }
            }}>
              <CardContent sx={{ p:2.5 }}>
                <Box display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                  <Box sx={{ bgcolor:report.bg, color:report.color, borderRadius:1.5, p:1, display:'flex' }}>
                    {React.cloneElement(report.icon, { sx:{ fontSize:22 } })}
                  </Box>
                  <Box flex={1}>
                    <Typography fontWeight={700} fontSize={14} color={report.color} lineHeight={1.3}>
                      {report.label}
                    </Typography>
                    {report.adminOnly && (
                      <Chip label="Admin Only" size="small" sx={{ fontSize:9, height:16, mt:0.3, bgcolor:'#FEF2F2', color:'#B91C1C' }}/>
                    )}
                  </Box>
                </Box>
                <Typography fontSize={12} color="text.secondary" mb={2} lineHeight={1.5}>
                  {report.desc}
                </Typography>
                <Button fullWidth variant="contained" size="small"
                  onClick={() => downloadReport(report.id)}
                  disabled={loading[report.id]}
                  startIcon={loading[report.id] ? <CircularProgress size={14} sx={{color:'white'}}/> : <Download sx={{fontSize:16}}/>}
                  sx={{ bgcolor:report.color, '&:hover':{ bgcolor:report.color, filter:'brightness(0.9)' },
                    borderRadius:1.5, fontWeight:600, fontSize:12, textTransform:'none', py:0.8 }}>
                  {loading[report.id] ? 'Generating...' : 'Download Excel'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
